/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useFilters, useUpdateURLSearchParams } from 'hooks/useQuery'
import * as React from 'react'
import tw from 'tailwind-styled-components'

import useSelectPagination from '@/components/SendResource/hooks/useSelectPagination'
import { useLoadFormsAndDocuments } from '@/queries/forms/useLoadForms'
import { useExportReports } from '@/queries/reports/useExportReportsAsCSV'
import type { ReportFilterProps } from '@/types/reports'
import Button from '@/ui/atoms/Button'
import Grid from '@/ui/atoms/Grid'
import DateRangeField from '@/ui/molecules/DateRangeField'
import SelectField from '@/ui/molecules/SelectField'
import TextField from '@/ui/molecules/TextField'
import Select from '@/ui/Select'

const FilterRow = tw.div`
  my-4
`

const DEFAULT_FILTERS = {
  user: '',
  packet: '',
  form: '',
  document: '',
  is_archived: '',
  is_completed: '',
  sent_at__gte: '',
  sent_at__lte: '',
  completed_at__gte: '',
  completed_at__lte: '',
  search: '',
  method: '',
}

function ReportsFilter(): React.ReactNode {
  const [hasMore, setHasMore] = React.useState(false)
  const {
    params,
    onInputChange,
    onMenuScrollToBottom,
    setOptions,
    options: formOptions,
  } = useSelectPagination(hasMore)
  const { filters }: { filters: ReportFilterProps } = useFilters()
  const updateParams = useUpdateURLSearchParams()

  const {
    data: formsAndDocuments,
    isLoading: formsAndDocumentsLoading,
    isFetching: formsAndDocumentsFetching,
    refetch: refetchFormsAndDocuments,
  } = useLoadFormsAndDocuments(
    { ...params },
    {
      staleTime: Infinity,
      keepPreviousData: true,
    }
  )
  const { exportReports, isLoading: isExporting } = useExportReports()

  React.useEffect(() => {
    setHasMore(Boolean(formsAndDocuments?.next))
  }, [formsAndDocuments?.next])

  React.useEffect(() => {
    if (formsAndDocuments?.results) {
      // @ts-ignore
      setOptions((options) => [
        ...options,
        ...formsAndDocuments.results.map((form) => ({
          value: form.id,
          label: form.name,
          type: form.type,
        })),
      ])
    }
  }, [formsAndDocuments?.results, setOptions])

  async function onFormSelected(
    option: { value: string; label: string; type: string } | null
  ) {
    onInputChange('')
    if (option?.type === 'form') {
      updateParams(['form', option.value])
    } else if (option?.type === 'pdf') {
      updateParams(['document', option.value])
    } else {
      updateParams(['form', ''])
      updateParams(['document', ''])
    }
    await refetchFormsAndDocuments()
  }

  const parseDateFilters = React.useCallback((start: string, end: string) => {
    if (!start && !end) return undefined

    let gteDate: Date | null = new Date(start || '')
    let lteDate: Date | null = new Date(end || '')

    if (gteDate && gteDate.toString() === 'Invalid Date') {
      gteDate = null
    }

    if (lteDate && lteDate.toString() === 'Invalid Date') {
      lteDate = null
    }

    return [gteDate, lteDate]
  }, [])

  const sentAtValue = React.useMemo(() => {
    const gte = filters.sent_at__gte
    const lte = filters.sent_at__lte

    return parseDateFilters(gte || '', lte || '')
  }, [filters.sent_at__gte, filters.sent_at__lte, parseDateFilters])

  const completedAtValue = React.useMemo(() => {
    const gte = filters.completed_at__gte
    const lte = filters.completed_at__lte

    return parseDateFilters(gte || '', lte || '')
  }, [filters.completed_at__gte, filters.completed_at__lte, parseDateFilters])

  return (
    <FilterRow>
      <div className="mt-1 flex-1 border border-gray-200 bg-gray-50 px-2 pb-2">
        <div className="mx-auto max-w-screen-xl px-4 py-4">
          {/* @ts-ignore */}
          <Grid className="mb-2" cols={2} gap="small">
            <TextField
              useEvent
              label="Client Name"
              onChange={(e: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const value: string = e.target.value
                updateParams(['search', value])
              }}
              value={filters.search || ''}
            />
            <div>
              {/* @ts-ignore */}
              <Select
                semibold
                showOptions
                showPlaceholder
                disabled={formsAndDocumentsLoading}
                isFetching={formsAndDocumentsFetching}
                label="Form"
                loading={formsAndDocumentsLoading}
                // @ts-ignore
                onChange={onFormSelected}
                onMenuScrollToBottom={onMenuScrollToBottom}
                onType={onInputChange}
                // @ts-ignore
                options={formOptions}
                placeholder="-- Select --"
              />
            </div>
          </Grid>
          {/* @ts-ignore */}
          <Grid className="mb-2" cols={2} gap="small">
            <DateRangeField
              semibold
              showPastPresets
              utcValue
              label="Sent Date"
              onChange={(value) => {
                if (value !== null) {
                  const gte: string =
                    value[0][0]?.toISOString().split('T')[0] || ''
                  const lte: string =
                    value[0][1]?.toISOString().split('T')[0] || ''

                  updateParams(['sent_at__gte', gte], ['sent_at__lte', lte])
                } else {
                  updateParams(['sent_at__gte', ''], ['sent_at__lte', ''])
                }
              }}
              // @ts-ignore
              value={sentAtValue}
            />
            <DateRangeField
              semibold
              showPastPresets
              utcValue
              label="Completed Date"
              onChange={(value) => {
                if (value !== null) {
                  const gte: string =
                    value[0][0]?.toISOString().split('T')[0] || ''
                  const lte: string =
                    value[0][1]?.toISOString().split('T')[0] || ''

                  updateParams(
                    ['completed_at__gte', gte],
                    ['completed_at__lte', lte]
                  )
                } else {
                  updateParams(
                    ['completed_at__gte', ''],
                    ['completed_at__lte', '']
                  )
                }
              }}
              // @ts-ignore
              value={completedAtValue}
            />
          </Grid>
          <Grid cols={3} gap="small">
            <SelectField
              // @ts-ignore
              semibold
              showPlaceholder
              label="Status"
              onChange={(event: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                event.persist()
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const value: string = event.target.value
                updateParams([
                  'is_completed',
                  value === 'Yes' ? 'true' : value === 'No' ? 'false' : '',
                ])
              }}
              options={[
                { value: 'Yes', label: 'Completed' },
                { value: 'No', label: 'Not Completed' },
              ]}
              value={
                String(filters.is_completed) === 'true'
                  ? 'Yes'
                  : String(filters.is_completed) === 'false'
                    ? 'No'
                    : ''
              }
            />
            <SelectField
              // @ts-ignore
              semibold
              showPlaceholder
              label="Method"
              onChange={(event: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                event.persist()
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const value: string = event.target.value
                updateParams(['method', value])
              }}
              options={[
                { value: 'Email', label: 'Email' },
                { value: 'SMS', label: 'SMS' },
                { value: 'Public URL', label: 'Public URL' },
                { value: 'Kiosk', label: 'Kiosk' },
                { value: 'Tablet', label: 'Tablet' },
              ]}
              value={filters.method || ''}
            />
            <SelectField
              // @ts-ignore
              semibold
              showPlaceholder
              label="Archived"
              onChange={(event: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
                event.persist()
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                const value: string = event.target.value
                updateParams([
                  'is_archived',
                  value === 'Yes' ? 'true' : value === 'No' ? 'false' : '',
                ])
              }}
              options={[
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]}
              value={
                String(filters.is_archived) === 'true'
                  ? 'Yes'
                  : String(filters.is_archived) === 'false'
                    ? 'No'
                    : ''
              }
            />
          </Grid>

          <div className="mt-4 flex gap-2">
            <Button
              $noOutline
              className="bg-white font-normal text-gray-500"
              onClick={() => {
                updateParams(
                  // @ts-ignore
                  ...Object.entries(DEFAULT_FILTERS).map(([key, value]) => [
                    key,
                    value,
                  ])
                )
              }}
            >
              Reset Filters
            </Button>

            <Button
              $noOutline
              className="bg-white font-normal text-gray-500"
              disabled={isExporting}
              loading={isExporting}
              onClick={async () => {
                await exportReports(filters)
              }}
            >
              Export CSV
            </Button>
          </div>
        </div>
      </div>
    </FilterRow>
  )
}

export default ReportsFilter
